import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { BadgeCheck, MapPin, Star, Store, Phone, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { ProductCard } from "@/components/marketplace/ProductCard";
import { LoadingState } from "@/components/common/States";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function VendorStore() {
  const { slug } = useParams();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/vendors/${slug}`).then((r) => setData(r.data));
  }, [slug]);

  if (!data) return <LoadingState label="Loading store..." />;
  const v = data.vendor;

  return (
    <div>
      <div className="relative h-48 sm:h-60 bg-muted">
        <img src={v.banner} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="-mt-14 relative flex flex-col sm:flex-row sm:items-end gap-4 pb-6">
          <div className="h-28 w-28 rounded-2xl border-4 border-background overflow-hidden bg-muted shadow-md">
            <img src={v.logo} alt={v.name} className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-stone-900">{v.name}</h1>
              {v.verified && <BadgeCheck size={22} className="text-emerald-600" data-testid="vendor-verified-badge" />}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-stone-600">
              <span className="flex items-center gap-1"><Star size={14} className="fill-primary text-primary" strokeWidth={0} /> {v.rating} ({v.reviews_count})</span>
              <span className="flex items-center gap-1"><MapPin size={14} /> {v.location}</span>
              <span className="flex items-center gap-1"><Store size={14} /> {v.category}</span>
              <span className="text-stone-400">Since {v.joined}</span>
            </div>
          </div>
          <a href="#" onClick={(e) => e.preventDefault()} data-testid="contact-vendor-btn" className="inline-flex items-center gap-2 rounded-full border border-secondary text-secondary font-semibold px-5 py-2.5 hover:bg-secondary hover:text-white transition-colors">
            <Phone size={16} /> Contact Vendor
          </a>
        </div>

        <div className="pb-12">
          <Tabs defaultValue="products">
            <TabsList>
              <TabsTrigger value="products" data-testid="vendor-tab-products">Products ({data.products.length})</TabsTrigger>
              <TabsTrigger value="about" data-testid="vendor-tab-about">About</TabsTrigger>
              <TabsTrigger value="policies" data-testid="vendor-tab-policies">Policies</TabsTrigger>
            </TabsList>

            <TabsContent value="products" className="pt-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {data.products.map((p) => <ProductCard key={p.slug} product={p} />)}
              </div>
            </TabsContent>

            <TabsContent value="about" className="pt-6 max-w-3xl">
              <h3 className="font-serif font-semibold text-xl text-stone-900 mb-2">About {v.name}</h3>
              <p className="text-stone-600 leading-relaxed">{v.description}</p>
              <div className="mt-4 inline-block text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                Demo vendor — for development preview only, not a real registered business.
              </div>
            </TabsContent>

            <TabsContent value="policies" className="pt-6 max-w-3xl text-stone-600">
              <p>Shipping, returns and vendor-specific policies will be configurable by vendors in the next development phase.</p>
              <div className="mt-3 flex gap-4 text-sm">
                <Link to="/shipping-policy" className="text-secondary font-medium">Shipping Policy</Link>
                <Link to="/refund-policy" className="text-secondary font-medium">Refund Policy</Link>
                <Link to="/vendor-policy" className="text-secondary font-medium">Vendor Policy</Link>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
